// ゲームの一覧。ビルドのとき tools/build-games.mjs が public/catalog.json に書き出す。
import type { Locale, Period } from "@appful/sdk/protocol";

export type Localized = Record<Locale, string>;

export interface CatalogBoard {
  id: string;
  order: "asc" | "desc";
  min: number;
  max: number;
  periods: Period[];
  label: Localized;
}

export interface CatalogGame {
  id: string;
  status: "draft" | "published";
  version: string;
  title: Localized;
  /** 何をするかの1行（一覧のタイルに出る） */
  hook: Localized;
  description: Localized;
  /** 1回の遊びの時間（分）。[短いとき, 長いとき] */
  minutes: [number, number];
  /** long：続きを残して長く遊ぶゲーム（大きさとセーブの上限が大きい）。無ければ短いゲーム */
  length?: "short" | "long";
  /** 学びのゲームだけ：教科と学年 */
  subject?: "math" | "japanese" | "social" | "science" | "english";
  grade?: "e1" | "e2" | "e3" | "e4" | "e5" | "e6" | "jh";
  howToPlay: Localized;
  /** このゲームについての長めの説明（ゲームのページの説明の欄。段落は空行で区切る）。無いゲームもある */
  about?: Localized;
  /** うまく遊ぶコツ（2〜5 個）。無いゲームもある */
  tips?: Localized[];
  genre: string;
  orientation: "portrait" | "landscape" | "any";
  aspectRatio: string;
  /** 遊ぶ場所の住所からのパス（/g/<id>/<版>/index.html） */
  entry: string;
  /** 一覧の住所からのパス */
  thumbnail: string;
  /** 遊んでいる様子の短い動画（音なし・mp4）。一覧の住所からのパス。PC でタイルに指を乗せると流れる。無いゲームもある */
  preview?: string;
  leaderboards: CatalogBoard[];
  /** オンライン対戦の遊び方。無ければ対戦なし */
  online?: CatalogOnline;
  /** AI を使うゲームの印（game.json の ai: true）。使えるかは関数が設定 config/ai で決める（docs/AI.md） */
  ai?: boolean;
  sizeBytes: number;
}

export interface CatalogMode {
  id: string;
  label: Localized;
  /** [最少, 最多] の人数 */
  players: [number, number];
  /** 0：全員が敵。1：全員が味方（協力）。2 以上：その数のチームに分ける */
  teams: number;
}

export interface CatalogOnline {
  modes: CatalogMode[];
  /** 最多の人数が揃わないとき、最少の人数で始めるまで待つ秒数 */
  waitSeconds?: number;
}

let loading: Promise<CatalogGame[]> | null = null;

export function loadCatalog(): Promise<CatalogGame[]> {
  loading ??= fetch("/catalog.json", { cache: "no-cache" })
    .then((r) => {
      if (!r.ok) throw new Error(`catalog ${r.status}`);
      return r.json() as Promise<{ games: CatalogGame[] }>;
    })
    .then((c) => c.games)
    .catch((e) => {
      loading = null;
      throw e;
    });
  return loading;
}

export async function findGame(id: string): Promise<CatalogGame | undefined> {
  return (await loadCatalog()).find((g) => g.id === id);
}
